/**
 * The focus board (spec 2026-10-14-workspaces): action items in five columns,
 * This week · Next · Later · Waiting · Done. Contract spellings:
 * `GET /api/board?sphere=&project=` and the bucket/boardRank/status
 * transitions `PUT /api/tasks/:id` accepts. Pure helpers are exported for tests.
 */
import { buildQuery, request } from '../api.js';
import { daysBetween } from '../lifeos/format.js';
import type { TaskInput, TaskPatch, TaskRow } from '../lifeos/types.js';

export type Bucket = 'this_week' | 'next' | 'later';
export type ColumnKey = Bucket | 'waiting' | 'done';

export interface BoardItem extends TaskRow {
  /** null = not placed yet; the server shows it under Next. */
  bucket: Bucket | null;
  boardRank: number | null;
  projectName: string | null;
  /** "Ferguson › SEO brief". */
  projectPath: string | null;
  /** Optional: the linked contact's name, for the avatar initial. */
  contactName?: string | null;
}

export type BoardColumns = Record<ColumnKey, BoardItem[]>;

export const COLUMNS: readonly { key: ColumnKey; label: string; empty: string; quickAdd: boolean }[] = [
  { key: 'this_week', label: 'This week', empty: 'Pull something in for this week', quickAdd: true },
  { key: 'next', label: 'Next', empty: 'Nothing queued', quickAdd: true },
  { key: 'later', label: 'Later', empty: 'Nothing parked', quickAdd: true },
  { key: 'waiting', label: 'Waiting', empty: 'Not waiting on anyone', quickAdd: false },
  { key: 'done', label: 'Done', empty: 'Done items show here for a week', quickAdd: false },
];

export const COLUMN_LABEL: Record<ColumnKey, string> = Object.fromEntries(COLUMNS.map((c) => [c.key, c.label])) as Record<ColumnKey, string>;

const KEYS: readonly ColumnKey[] = COLUMNS.map((c) => c.key);

export function emptyColumns(): BoardColumns {
  return { this_week: [], next: [], later: [], waiting: [], done: [] };
}

/** `GET /api/board`; a column the server leaves out reads as empty. */
export async function getBoard(q: { sphere?: string | null; project?: string | null } = {}): Promise<BoardColumns> {
  const r = await request<{ columns?: Partial<BoardColumns> }>(`/board${buildQuery({ sphere: q.sphere, project: q.project })}`);
  const out = emptyColumns();
  for (const k of KEYS) out[k] = r.columns?.[k] ?? [];
  return out;
}

export type BoardPatch = TaskPatch & { bucket?: Bucket | null; boardRank?: number | null };

export async function moveTask(id: string, patch: BoardPatch): Promise<TaskRow> {
  return (await request<{ task: TaskRow }>(`/tasks/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(patch) })).task;
}

/**
 * Create an action item straight into a bucket. If the server's create ignores
 * `bucket` (older build), the bucket is set with a follow-up PUT.
 */
export async function createBoardTask(body: TaskInput & { bucket: Bucket }): Promise<TaskRow> {
  const task = await request<{ task: TaskRow & { bucket?: Bucket | null } }>('/tasks', { method: 'POST', body: JSON.stringify(body) });
  if (task.task.bucket !== body.bucket) return moveTask(task.task.id, { bucket: body.bucket });
  return task.task;
}

/** Which column a row sits in. */
export function columnOf(t: Pick<BoardItem, 'status' | 'bucket'>): ColumnKey {
  if (t.status === 'done') return 'done';
  if (t.status === 'waiting') return 'waiting';
  // Unbucketed someday rows sit under Later (matches server board/board.ts columnOf).
  return t.bucket ?? (t.status === 'someday' ? 'later' : 'next');
}

/** A rank strictly between two neighbours (either may be missing). */
export function rankBetween(before: number | null, after: number | null): number {
  if (before === null && after === null) return 1000;
  if (before === null) return after! - 1000;
  if (after === null) return before + 1000;
  return (before + after) / 2;
}

/** The ranks a column's rows sort by: their own, or one derived from their position. */
function effectiveRanks(rows: readonly BoardItem[]): number[] {
  const out: number[] = [];
  rows.forEach((r, i) => {
    const prev = i > 0 ? out[i - 1]! : null;
    const own = r.boardRank;
    out.push(own !== null && own !== undefined && (prev === null || own > prev) ? own : rankBetween(prev, null));
  });
  return out;
}

/** The rank for a row dropped at `index` in `rows` (which must not contain the row itself). */
export function rankAt(rows: readonly BoardItem[], index: number): number {
  const ranks = effectiveRanks(rows);
  const i = Math.max(0, Math.min(index, rows.length));
  return rankBetween(i > 0 ? ranks[i - 1]! : null, i < rows.length ? ranks[i]! : null);
}

/**
 * The PUT body for a move (contract transitions):
 * - into Waiting: `{status:'waiting'}`; into Done: `{status:'done'}`;
 * - out of Waiting/Done into a bucket: `{status:'next', bucket, boardRank}`;
 * - between buckets: `{bucket, boardRank}` (plus `status:'next'` for a to-sort/someday row,
 *   except a someday row going to Later, which stays someday);
 * - within a column: `{boardRank}`.
 */
export function moveBody(t: Pick<BoardItem, 'status' | 'bucket'>, to: ColumnKey, boardRank: number): BoardPatch {
  const from = columnOf(t);
  if (to === 'done') return from === 'done' ? { boardRank } : { status: 'done' };
  if (to === 'waiting') return from === 'waiting' ? { boardRank } : { status: 'waiting' };
  if (from === to && t.bucket === to) return { boardRank };
  if (t.status === 'someday' && to === 'later') return { bucket: to, boardRank };
  if (t.status !== 'next') return { status: 'next', bucket: to, boardRank };
  return { bucket: to, boardRank };
}

/** The board after moving `id` to `to` at `index` (optimistic state), plus the moved row's new fields. */
export function applyMove(
  cols: BoardColumns,
  id: string,
  to: ColumnKey,
  index: number,
): { cols: BoardColumns; item: BoardItem; body: BoardPatch; fixes: { id: string; boardRank: number }[] } | null {
  let found: BoardItem | null = null;
  let fromKey: ColumnKey | null = null;
  let fromIndex = -1;
  for (const k of KEYS) {
    const i = cols[k].findIndex((x) => x.id === id);
    if (i >= 0) {
      found = cols[k][i]!;
      fromKey = k;
      fromIndex = i;
      break;
    }
  }
  if (!found || !fromKey) return null;
  const next: BoardColumns = { ...cols, [fromKey]: cols[fromKey].filter((x) => x.id !== id) };
  // Dropping below itself in the same column: the row's own slot disappears first.
  const at = fromKey === to && index > fromIndex ? index - 1 : index;
  if (fromKey === to && at === fromIndex) return null;
  const target = next[to];
  const rank = rankAt(target, at);
  const body = moveBody(found, to, rank);
  // The server sorts by board_rank NULLS LAST, so the new rank only holds its
  // slot if every card above the drop point carries the rank it is displayed
  // at. Pin those (unranked, or tied/out of order) with their effective rank.
  const fixes: { id: string; boardRank: number }[] = [];
  if (body.boardRank !== undefined) {
    const ranks = effectiveRanks(target);
    target.slice(0, Math.max(0, Math.min(at, target.length))).forEach((r, i) => {
      if (r.boardRank !== ranks[i]) fixes.push({ id: r.id, boardRank: ranks[i]! });
    });
  }
  const fixed = new Map(fixes.map((f) => [f.id, f.boardRank]));
  const item: BoardItem = {
    ...found,
    boardRank: rank,
    status: to === 'done' ? 'done' : to === 'waiting' ? 'waiting' : body.status === 'next' ? 'next' : found.status,
    bucket: to === 'done' || to === 'waiting' ? found.bucket : to,
  };
  const list = target.map((r) => (fixed.has(r.id) ? { ...r, boardRank: fixed.get(r.id)! } : r));
  list.splice(Math.max(0, Math.min(at, list.length)), 0, item);
  next[to] = list;
  return { cols: next, item, body, fixes };
}

/** Due tone on a card: red when late, amber within two days. */
export function dueTone(dueOn: string | null, today: string): 'overdue' | 'soon' | 'later' | null {
  if (!dueOn) return null;
  const n = daysBetween(today, dueOn);
  if (n < 0) return 'overdue';
  if (n <= 2) return 'soon';
  return 'later';
}

export const PRIORITY_WORD: Record<number, string> = { 1: 'High priority', 2: 'Normal priority', 3: 'Low priority' };

// --- Swimlanes ------------------------------------------------------------------

/** 'project': one job selected → a lane per top-level project + Misc. 'sphere': All → a lane per job. */
export type LaneMode = 'project' | 'sphere';

/** The minimum a lane needs to know about a project (Project rows; `priority` when the server sends it). */
export interface LaneProject {
  id: string;
  name: string;
  sphereKey: string;
  parentId: string | null;
  priority?: number | null;
  status?: string;
}

export interface LaneSphere {
  key: string;
  name: string;
  color: string | null;
}

export interface Lane {
  /** `p:<top project id>`, `n:<name>` (project not in the list), `misc`, or `s:<sphere key>`. */
  key: string;
  name: string;
  /** Job colour (sphere lanes, and project lanes take their job's). */
  color: string | null;
  /** The top-level project id this lane stands for (project lanes only). */
  projectId: string | null;
  cols: BoardColumns;
  count: number;
}

export const MISC_LANE = 'misc';

/** The top-level ancestor of a project (itself when top level); null when unknown. */
export function topProject(byId: ReadonlyMap<string, LaneProject>, id: string): LaneProject | null {
  let cur = byId.get(id);
  const seen = new Set<string>();
  while (cur && cur.parentId && byId.has(cur.parentId) && !seen.has(cur.id)) {
    seen.add(cur.id);
    cur = byId.get(cur.parentId);
  }
  return cur ?? null;
}

/** Which lane a row belongs to, and the lane's display name. */
export function laneOf(
  t: Pick<BoardItem, 'projectId' | 'projectPath' | 'projectName' | 'sphereKey'>,
  mode: LaneMode,
  byId: ReadonlyMap<string, LaneProject>,
): { key: string; name: string; projectId: string | null } {
  if (mode === 'sphere') return { key: `s:${t.sphereKey}`, name: t.sphereKey, projectId: null };
  if (!t.projectId) return { key: MISC_LANE, name: 'Misc', projectId: null };
  const top = topProject(byId, t.projectId);
  if (top) return { key: `p:${top.id}`, name: top.name, projectId: top.id };
  // Not in the project list (archived, or the list failed): group by the path's first name.
  const first = (t.projectPath ?? t.projectName ?? '').split('›')[0]!.trim() || 'Project';
  return { key: `n:${first}`, name: first, projectId: null };
}

/**
 * Split the board into lanes. Project mode: lanes by top-level project
 * (priority 1 first, then name), Misc last. Sphere mode: lanes in the job
 * order given. With `showEmpty`, every listed project/job gets a lane even
 * when it has no rows; otherwise empty lanes are left out.
 */
export function buildLanes(
  cols: BoardColumns,
  mode: LaneMode,
  projects: readonly LaneProject[],
  spheres: readonly LaneSphere[],
  opts: { sphere?: string | null; showEmpty?: boolean } = {},
): Lane[] {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const sphereBy = new Map(spheres.map((s) => [s.key, s]));
  const lanes = new Map<string, Lane>();
  const ensure = (key: string, name: string, projectId: string | null, color: string | null): Lane => {
    let l = lanes.get(key);
    if (!l) {
      l = { key, name, color, projectId, cols: emptyColumns(), count: 0 };
      lanes.set(key, l);
    }
    return l;
  };
  for (const k of KEYS) {
    for (const t of cols[k]) {
      const at = laneOf(t, mode, byId);
      const s = sphereBy.get(t.sphereKey);
      const name = mode === 'sphere' ? s?.name ?? t.sphereKey : at.name;
      const l = ensure(at.key, name, at.projectId, s?.color ?? null);
      l.cols[k].push(t);
      l.count += 1;
    }
  }
  if (opts.showEmpty) {
    if (mode === 'sphere') for (const s of spheres) ensure(`s:${s.key}`, s.name, null, s.color);
    else {
      for (const p of projects) {
        if (p.parentId && byId.has(p.parentId)) continue;
        if (opts.sphere && p.sphereKey !== opts.sphere) continue;
        if (p.status === 'archived' || p.status === 'done') continue;
        ensure(`p:${p.id}`, p.name, p.id, sphereBy.get(p.sphereKey)?.color ?? null);
      }
      ensure(MISC_LANE, 'Misc', null, null);
    }
  }
  const out = [...lanes.values()];
  if (mode === 'sphere') {
    const order = new Map(spheres.map((s, i) => [`s:${s.key}`, i]));
    return out.sort((a, b) => (order.get(a.key) ?? 1e9) - (order.get(b.key) ?? 1e9) || a.name.localeCompare(b.name));
  }
  const pri = (l: Lane): number => (l.projectId ? byId.get(l.projectId)?.priority ?? 2 : 2);
  return out.sort((a, b) => {
    if (a.key === MISC_LANE) return 1;
    if (b.key === MISC_LANE) return -1;
    return pri(a) - pri(b) || a.name.localeCompare(b.name);
  });
}

/**
 * A lane-local drop position → the index in the whole column, which is what
 * `applyMove` ranks against. `laneIds` are the lane's rows in that column, in
 * order (including the dragged row if it is there). Past the lane's last row
 * means just after it; a lane with no rows there drops at the column's end.
 */
export function laneToColumnIndex(column: readonly Pick<BoardItem, 'id'>[], laneIds: readonly string[], laneIndex: number): number {
  if (laneIds.length === 0) return column.length;
  if (laneIndex < laneIds.length) {
    const i = column.findIndex((x) => x.id === laneIds[Math.max(0, laneIndex)]);
    return i < 0 ? column.length : i;
  }
  const last = column.findIndex((x) => x.id === laneIds[laneIds.length - 1]);
  return last < 0 ? column.length : last + 1;
}
